import { cn } from '#/shared/lib/utils'
import { parseSort } from '#/shared/lib/list-sort'
import { ChevronDownIcon } from '#/shared/ui/icons'

/**
 * A column heading you can order by.
 *
 * The signal is carried by the label, not by the arrow. Header text is already
 * `text-gray-900 font-medium`, so making the active column darker says nothing
 * — the first version leaned on a heavy filled triangle instead, and it became
 * the loudest mark in the header row. Here the sorted column goes semibold and
 * its chevron sits quietly beside it; on the others the chevron appears only
 * under the cursor, so a ten-column table is not a row of little arrows.
 *
 * One glyph, rotated, for both directions. The first version mixed two icon
 * families — a 9×6 filled triangle for the active state and a 16×20 stroked
 * double-chevron for the inactive one — which at 10px rendered as a blob next
 * to a hairline, and shifted the column width every time the sort changed.
 */
export function SortableHeader({
  label,
  field,
  sort,
  onToggle,
}: {
  label: string
  field: string
  sort: string | null | undefined
  onToggle: () => void
}) {
  const active = parseSort(sort)
  const isActive = active?.field === field

  return (
    <button
      type="button"
      onClick={onToggle}
      title={`Sort by ${label}`}
      // Spans the cell's free space rather than just the text: a header is
      // aimed at roughly, and a label-width target means clicking a hair to
      // the right of "Organization" does nothing.
      className={cn(
        'group/sort inline-flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-left select-none',
        isActive && 'font-semibold',
      )}
    >
      {label}
      {/* The box is always there so the header keeps its width whether or not
          the chevron is showing — otherwise every sort nudges the columns. */}
      <span className="flex size-3 shrink-0 items-center justify-center">
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'size-2.5 transition-[transform,opacity,color]',
            isActive
              ? 'text-gray-500 opacity-100'
              : 'text-gray-400 opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100',
            isActive && active.dir === 'asc' && 'rotate-180',
          )}
        />
      </span>
    </button>
  )
}
